import type React from 'react';
import { memo, useEffect, useRef, useState } from 'react';
import { formatMoney } from '../format';

export interface BalanceBarProps {
  balance: number;
  bet: number;
  currency?: string;
}

// Memoized — its props (balance/bet/currency) don't change on the
// high-frequency drag/next-loading state updates that otherwise cause
// SlotUI's whole chrome tree to re-render on every swipe frame.
export const BalanceBar = memo(function BalanceBar({
  balance,
  bet,
  currency = '$',
}: BalanceBarProps) {
  return (
    <div
      style={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        // Padding scales down a touch on narrow screens so the bar never
        // feels cramped against its own edges.
        padding: 'clamp(6px, 2vw, 8px) clamp(12px, 4vw, 18px)',
        position: 'absolute',
        // Fluid width instead of a hard-coded 382px: fills the container
        // with 16px of breathing room on each side, but never grows past
        // the original design width. Centered via the `left/right: 0` +
        // `margin: 0 auto` absolute-centering trick, so this works at any
        // container size — phones, tablets, or a resized embed — not just
        // the 390px reference frame.
        left: 0,
        right: 0,
        margin: '0 auto',
        width: 'min(382px, calc(100% - 32px))',
        minHeight: 52,
        bottom: 'clamp(16px, 5vh, 38px)',
        background: 'rgba(9, 4, 24, 0.37)',
        borderRadius: 10,
        zIndex: 10,
      }}
    >
      {/* Balance */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
          Balance
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <CoinIcon />
          <span
            style={{
              fontSize: 'clamp(13px, 4vw, 16px)',
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {formatMoney(balance, currency)}
          </span>
        </span>
      </div>

      <div style={{ flex: 1 }} />

      {/* Bet */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
          Bet
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <CoinIcon />
          <span
            style={{
              fontSize: 'clamp(13px, 4vw, 16px)',
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {formatMoney(bet, currency)}
          </span>
        </span>
      </div>

    </div>
  );
});

// Shown in the bottom bar. Reads "loading next slot" with a spinner while
// the queued reel initializes; swaps to "Swipe up for the next game" once
// ready. When status is 'loading', `progress` (0-100) fills the text
// left-to-right with the accent colour.
export function NextSlotHint({
  status,
  drag,
  progress = 0,
}: {
  status: 'loading' | 'ready';
  drag: { offset: number; dragging: boolean };
  progress?: number;
}) {
  const label = status === 'ready' ? 'Swipe up for the next game' : 'loading next slot';
  const PARALLAX = 0.25;
  // Only react to an upward drag (negative offset) — that's the gesture
  // this label is actually about. `drag.offset` is also positive while the
  // player swipes *down* to open the history panel (see ReelsStack.tsx),
  // and this text has nothing to do with that gesture, so it should stay
  // put rather than sliding along with it.
  const parallaxOffset = drag.offset < 0 ? drag.offset : 0;

  // Blink once when status transitions to 'ready' to draw the player's eye.
  const prevStatus = useRef(status);
  const [blinking, setBlinking] = useState(false);
  useEffect(() => {
    if (prevStatus.current !== 'ready' && status === 'ready') {
      setBlinking(true);
      const t = setTimeout(() => setBlinking(false), 700);
      return () => clearTimeout(t);
    }
    prevStatus.current = status;
  }, [status]);

  const wrapperStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    transform: `translateY(${parallaxOffset * PARALLAX}px)`,
    transition: drag.dragging
      ? 'none'
      : 'transform 0.38s cubic-bezier(0.22, 1, 0.36, 1)',
    willChange: drag.dragging ? 'transform' : 'auto',
  };

  if (status === 'ready') {
    return (
      <span className={blinking ? 'reelay-nsh-blink' : undefined} style={{ ...wrapperStyle, color: 'var(--accent, #FE37B4)' }}>
        {label}
      </span>
    );
  }

  // Loading state: two-layer text fill representing progress.
  return (
    <span className={blinking ? 'reelay-nsh-blink' : undefined} style={wrapperStyle}>
      <Spinner />
      {/* Stacked text: dim base + accent fill clipped to progress width */}
      <span style={{ position: 'relative', display: 'inline-block' }}>
        {/* Base layer — unfilled portion */}
        <span style={{ color: 'rgba(255,255,255,0.28)' }}>{label}</span>
        {/* Fill layer — accent, clipped from the left */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            color: 'var(--accent, #FE37B4)',
            clipPath: `inset(0 ${100 - progress}% 0 0)`,
            transition: 'clip-path 0.25s ease-out',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </span>
    </span>
  );
}

function CoinIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="9" fill="#F5A623"/>
      <circle cx="9" cy="9" r="7" fill="#F7B731"/>
      <text x="9" y="13" textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="Montserrat,sans-serif" fill="#C47D0E">$</text>
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      className="reelay-spinner"
      width="10"
      height="10"
      viewBox="0 0 16 16"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="2"
      />
      <path
        d="M8 2 a6 6 0 0 1 6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
